import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  Calendar, Download, Users, CheckCircle, Clock, XCircle, 
  FileSpreadsheet, ChevronLeft, ChevronRight, Search 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AttendanceReport() {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [selectedMonth]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/staff-attendance/report/monthly?month=${selectedMonth}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setReport(data);
      } else {
        toast.error('Failed to fetch report');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to fetch attendance report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/staff-attendance/report/export?month=${selectedMonth}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Convert to CSV
        const exportData = data.export_data || [];
        if (exportData.length === 0) {
          toast.error('No data to export');
          return;
        }

        const headers = Object.keys(exportData[0]);
        const csvContent = [
          headers.join(','),
          ...exportData.map(row => 
            headers.map(h => `"${row[h] || ''}"`).join(',')
          )
        ].join('\n');

        // Download
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `attendance_report_${selectedMonth}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast.success('Report exported successfully!');
      }
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    let newMonth = month + direction;
    let newYear = year;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const getPercentageColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600 bg-green-50';
    if (percentage >= 75) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const filteredStaff = (report?.staff_report || []).filter(staff => 
    staff.staff_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6" data-testid="attendance-report-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Staff Attendance Report</h1>
          <p className="text-gray-500">Monthly attendance summary for all staff</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Month Navigator */}
          <div className="flex items-center gap-2 bg-white border rounded-lg px-2">
            <button 
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-2 py-2 border-0 focus:ring-0"
            />
            <button 
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-gray-100 rounded"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          
          <Button 
            onClick={handleExport} 
            disabled={exporting || loading}
            className="bg-green-600 hover:bg-green-700"
            data-testid="export-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : report ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <Users className="h-6 w-6 text-blue-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-blue-700">{report.total_staff}</p>
                <p className="text-xs text-blue-600">Total Staff</p>
              </CardContent>
            </Card>
            
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-green-700">{report.summary_totals?.present || 0}</p>
                <p className="text-xs text-green-600">Total Present</p>
              </CardContent>
            </Card>
            
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4 text-center">
                <Clock className="h-6 w-6 text-yellow-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-yellow-700">{report.summary_totals?.late || 0}</p>
                <p className="text-xs text-yellow-600">Total Late</p>
              </CardContent>
            </Card>
            
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 text-center">
                <XCircle className="h-6 w-6 text-red-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-red-700">{report.summary_totals?.absent || 0}</p>
                <p className="text-xs text-red-600">Total Absent</p>
              </CardContent>
            </Card>
            
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4 text-center">
                <Calendar className="h-6 w-6 text-purple-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-purple-700">{report.total_working_days}</p>
                <p className="text-xs text-purple-600">Working Days</p>
              </CardContent>
            </Card>
          </div>

          {/* Month Info */}
          <div className="flex items-center justify-between bg-indigo-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-8 w-8 text-indigo-600" />
              <div>
                <h3 className="font-semibold text-indigo-900">
                  {report.month_name} {report.year}
                </h3>
                <p className="text-sm text-indigo-600">
                  {report.total_working_days} working days • {report.holidays_count} holidays
                </p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, ID, or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
          </div>

          {/* Report Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Staff-wise Attendance ({filteredStaff.length} staff)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Present</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Late</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Half Day</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Absent</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Leave</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Attendance %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredStaff.map((staff) => (
                      <tr key={staff.staff_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900">{staff.staff_name}</p>
                            <p className="text-xs text-gray-500">{staff.employee_id}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {staff.department || '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-medium text-green-600">{staff.present}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-medium text-yellow-600">{staff.late}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-medium text-orange-600">{staff.half_day}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-medium text-red-600">{staff.absent}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-medium text-blue-600">{staff.leave}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded text-sm font-medium ${getPercentageColor(staff.attendance_percentage)}`}>
                            {staff.attendance_percentage}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredStaff.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No staff found matching your search</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-center py-16 text-gray-500">
          <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <p>No attendance data available for this month</p>
        </div>
      )}
    </div>
  );
}
